import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Modal from './Modal';
import { SUPABASE_URL, supabaseHeaders } from '../supabase';
import { uploadPoster } from '../utils/imageUpload';
import { useAuth } from '../AuthContext';

function PokemonList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [addName, setAddName] = useState('');
  const [addPoster, setAddPoster] = useState(null);
  const [addPosterPreview, setAddPosterPreview] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  const [addStatus, setAddStatus] = useState('');
  const [editingGame, setEditingGame] = useState(null);
  const [editName, setEditName] = useState('');
  const [editPoster, setEditPoster] = useState(null);
  const [editPosterPreview, setEditPosterPreview] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editStatus, setEditStatus] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const fetchGames = async () => {
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/pokemon_games?order=created_at.desc&select=*`, {
        headers: supabaseHeaders(false),
      });
      if (!res.ok) throw new Error('Failed to fetch');
      setGames(await res.json() || []);
    } catch (err) {
      console.error('Failed to fetch pokemon games:', err);
    }
  };

  useEffect(() => { fetchGames(); }, []);

  const openAdd = () => {
    setAddName('');
    setAddPoster(null);
    setAddPosterPreview(null);
    setAddStatus('');
    setShowAdd(true);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addName) { setAddStatus('Please fill in a name.'); return; }
    setAddLoading(true);
    setAddStatus('');
    try {
      let posterUrl = null;
      if (addPoster) posterUrl = await uploadPoster(addPoster);
      const res = await fetch(`${SUPABASE_URL}/rest/v1/pokemon_games`, {
        method: 'POST',
        headers: { ...supabaseHeaders(true), Prefer: 'return=representation' },
        body: JSON.stringify({ name: addName, poster_url: posterUrl }),
      });
      if (!res.ok) throw new Error('Failed to create');
      setAddStatus('Saved!');
      setTimeout(() => { setShowAdd(false); fetchGames(); }, 800);
    } catch (err) {
      setAddStatus(`Something went wrong: ${err.message}`);
    } finally {
      setAddLoading(false);
    }
  };

  const openEdit = (game) => {
    setEditingGame(game);
    setEditName(game.name);
    setEditPoster(null);
    setEditPosterPreview(null);
    setEditStatus('');
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editName) { setEditStatus('Please fill in a name.'); return; }
    setEditLoading(true);
    setEditStatus('');
    try {
      let posterUrl = null;
      if (editPoster) posterUrl = await uploadPoster(editPoster);

      const payload = { name: editName };
      if (posterUrl) payload.poster_url = posterUrl;

      const res = await fetch(`${SUPABASE_URL}/rest/v1/pokemon_games?id=eq.${editingGame.id}`, {
        method: 'PATCH',
        headers: supabaseHeaders(true),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Update failed');
      setEditStatus('Saved!');
      setTimeout(() => { setEditingGame(null); fetchGames(); }, 800);
    } catch (err) {
      setEditStatus(`Something went wrong: ${err.message}`);
    } finally {
      setEditLoading(false);
    }
  };

  const deleteGame = async (id) => {
    setDeletingId(id);
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/pokemon_games?id=eq.${id}`, {
        method: 'DELETE',
        headers: supabaseHeaders(true),
      });
      if (!res.ok) throw new Error('Delete failed');
      setEditingGame(null);
      setGames((prev) => prev.filter((g) => g.id !== id));
    } catch (err) {
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  const displayed = games.filter(g =>
    g.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="app-container">
      <div className="header">
        <div className="header-title-row">
          <h1>Pokémon</h1>
          <Link to="/pokemon/todo" className="header-nav-btn">To-Do</Link>
          <Link to="/pokemon/shinies" className="header-nav-btn">Shinies</Link>
          <Link to="/pokemon/soullink" className="header-nav-btn">Soul Link</Link>
          <Link to="/pokemon/types" className="header-nav-btn">Types</Link>
        </div>
        <div className="controls">
          {user && <button onClick={openAdd}>Add +</button>}
          {user && (
            <button onClick={() => setEditMode((m) => !m)} className={editMode ? 'btn-active' : ''}>
              Edit
            </button>
          )}
          <input
            type="text"
            placeholder="Search by game..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="todo-grid">
        {displayed.map((game, idx) => (
          <div
            className="todo-card"
            key={game.id}
            style={{ '--index': idx, cursor: 'pointer' }}
            onClick={() => (editMode ? openEdit(game) : navigate(`/pokemon/${game.id}`))}
          >
            {game.poster_url && (
              <img src={game.poster_url} alt={game.name} className="todo-card-poster" />
            )}
            <div className="todo-card-title">{game.name}</div>
          </div>
        ))}
      </div>

      {games.length === 0 && <p className="no-results">Geen ene tering hier man.</p>}

      {showAdd && (
        <Modal onClose={() => setShowAdd(false)}>
          <h2 className="modal-title">Add game</h2>
          <form className="modal-form" onSubmit={handleAdd}>
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                
                autoFocus
                required
              />
            </div>
            <div className="form-group">
              <label>Cover</label>
              {addPosterPreview && <img src={addPosterPreview} alt="preview" className="poster-preview" />}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files[0];
                  setAddPoster(f);
                  setAddPosterPreview(f ? URL.createObjectURL(f) : null);
                }}
              />
            </div>
            {addStatus && <p className="form-status">{addStatus}</p>}
            <div className="edit-modal-actions">
              <button type="submit" className="form-submit" disabled={addLoading}>
                {addLoading ? 'Working...' : 'Add'}
              </button>
              <button type="button" className="form-submit btn-cancel" onClick={() => setShowAdd(false)}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      {editingGame && (
        <Modal onClose={() => setEditingGame(null)}>
          <h2 className="modal-title">Edit game</h2>
          <form className="modal-form" onSubmit={handleEditSubmit}>
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                autoFocus
                required
              />
            </div>
            <div className="form-group">
              <label>Cover</label>
              {(editPosterPreview || (editingGame.poster_url && !editPoster)) && (
                <img src={editPosterPreview || editingGame.poster_url} alt="preview" className="poster-preview" />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files[0];
                  setEditPoster(f);
                  setEditPosterPreview(f ? URL.createObjectURL(f) : null);
                }}
              />
            </div>
            {editStatus && <p className="form-status">{editStatus}</p>}
            <div className="edit-modal-actions">
              <button type="button" className="form-submit btn-cancel" onClick={() => setEditingGame(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="form-submit btn-delete"
                disabled={deletingId === editingGame.id}
                onClick={() => deleteGame(editingGame.id)}
              >
                {deletingId === editingGame.id ? 'Working...' : 'Delete'}
              </button>
              <button type="submit" className="form-submit" disabled={editLoading}>
                {editLoading ? 'Working...' : 'Save'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default PokemonList;
